import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

interface DataReloadContextType {
  registerReloadFunction: (id: string, reloadFn: () => void) => void;
  unregisterReloadFunction: (id: string) => void;
  reloadData: (id?: string) => void;
}

const DataReloadContext = createContext<DataReloadContextType | undefined>(
  undefined
);

export const useDataReload = (): DataReloadContextType => {
  const context = useContext(DataReloadContext);
  if (!context) {
    throw new Error('useDataReload must be used within a DataReloadProvider');
  }
  return context;
};

interface DataReloadProviderProps {
  children: ReactNode;
}

export const DataReloadProvider: React.FC<DataReloadProviderProps> = ({
  children,
}) => {
  const [reloadFunctions, setReloadFunctions] = useState<
    Record<string, () => void>
  >({});

  const registerReloadFunction = useCallback(
    (id: string, reloadFn: () => void) => {
      setReloadFunctions(prev => ({
        ...prev,
        [id]: reloadFn,
      }));
    },
    []
  );

  const unregisterReloadFunction = useCallback((id: string) => {
    setReloadFunctions(prev => {
      const newFunctions = { ...prev };
      delete newFunctions[id];
      return newFunctions;
    });
  }, []);

  const reloadData = useCallback(
    (id?: string) => {
      // Use a ref to access current functions without causing re-renders
      const currentFunctions = reloadFunctions;

      if (id && currentFunctions[id]) {
        currentFunctions[id]();
      } else {
        // If no specific ID, reload all registered tables
        Object.values(currentFunctions).forEach(fn => fn());
      }
    },
    [reloadFunctions]
  );

  return (
    <DataReloadContext.Provider
      value={{
        registerReloadFunction,
        unregisterReloadFunction,
        reloadData,
      }}
    >
      {children}
    </DataReloadContext.Provider>
  );
};
